import * as React from 'react';
import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import adminUpdateUserRequest from '@careerstart/wae-common/schema/users/patch/admin.req.json';
import { USER_STATUS } from '@careerstart/wae-common/src/main/constants/USER_EDITABLE_FIELDS';
import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { validateEmail } from '@careerstart/wae-common/src/main/helperFunction/profileHelperss';
import { Box, Button, TextField, Typography } from '@mui/material';

import { selectSendVerificationEmailButtonDisabled } from '../../../../main-deprecated/store/selectors/accountVerificationSelector';
import theme from '../../../theme';
import { BLACK, PRIMARY_PURPLE } from '../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../theme/fontConstants';
import ResendVerificationEmailModal from '../../../views/usersList/userDetail/components/workerComponents/ResendVerificationModal';
import { updateUser } from '../../../views/usersList/usersListReducer';
import { validateSchema } from '../../Form/validations';

const styles = theme?.components?.userCard;

const EditableEmailField = ({ initialValue, userId, userStatus, disabled }) => {
  const dispatch = useDispatch();
  const isVerificationEmailButtonDisabled = useSelector(selectSendVerificationEmailButtonDisabled);
  const [email, setEmail] = useState(initialValue || '');
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');
  const [isResendEmailModalOpen, setIsResendEmailModalOpen] = useState(false);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setIsEditing(true);
    setError('');
  };

  const handleEmailSave = useCallback(() => {
    const validationError = validateEmail(email);
    setError(validationError);

    if (!validationError) {
      const fields = {
        email,
      };
      const data = { id: userId, fields };
      if (Object.keys(validateSchema(data, adminUpdateUserRequest)).length === 0) {
        dispatch(updateUser(data));
      }
      setIsEditing(false);
    }
  }, [email, userId, dispatch]);

  const handleOpenEmailVerificationModal = () => {
    setIsResendEmailModalOpen(true);
  };

  return (
    <Box display="flex" flexDirection="column" marginTop={2}>
      <Typography sx={styles.subTitleStyle}>
        {LanguageConverterV2('userCard.subTitle.email')}
      </Typography>
      <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
        <TextField
          disabled={disabled}
          value={email}
          onChange={handleEmailChange}
          inputProps={{ 'data-testid': 'email-input' }}
          sx={styles.editableEmailStyle}
        />
        <Button
          variant="outlined"
          onClick={handleEmailSave}
          data-testid="email-save-button"
          disabled={!isEditing || disabled}
          sx={{
            color: isEditing ? PRIMARY_PURPLE[70] : BLACK[60],
            fontFamily: PRIMARY_FONT[500],
            borderColor: isEditing ? PRIMARY_PURPLE[70] : BLACK[60],
            height: '40px',
          }}
        >
          {LanguageConverterV2('buttonText.save')}
        </Button>
      </Box>
      {userStatus === USER_STATUS.ONBOARDING_INCOMPLETE && (
        <Button
          variant="contained"
          onClick={handleOpenEmailVerificationModal}
          disabled={isVerificationEmailButtonDisabled}
          data-testid="resend-email-button"
          fullWidth
          sx={styles.deactivateButtonStyle}
        >
          {LanguageConverterV2('buttonText.resendVerification')}
        </Button>
      )}
      {error && (
        <Typography data-testid="email-error-message" sx={styles.errorMessage}>
          {LanguageConverterV2(error)}
        </Typography>
      )}
      <ResendVerificationEmailModal
        isOpen={isResendEmailModalOpen}
        onClose={() => setIsResendEmailModalOpen(false)}
        userID={userId}
        isDisabled={disabled}
        userEmail={email}
      />
    </Box>
  );
};

EditableEmailField.propTypes = {
  initialValue: PropTypes.string,
  userId: PropTypes.string.isRequired,
  userStatus: PropTypes.string,
  disabled: PropTypes.bool,
};

export default EditableEmailField;
